import Highcharts from 'highcharts'
import { SegmentTotalsRecord } from '@/components/Survey/Utilities/ChartUtilities'
import { QuestionOptions } from '@/components/Survey/Utilities/SurveyUtilities'
import { QuestionType } from '@shared/types/database'

export const slicedChartTooltipFormatter = (
  segmentTotals: SegmentTotalsRecord,
  questionType: string,
  sliceByQuestionType?: string
): Highcharts.TooltipOptions => {
  const isWeightedAvg = questionType === `${QuestionType.Rank}-weighted-average`
  const isRate = questionType === QuestionType.Rate || questionType === QuestionType.RateOnly || questionType === QuestionType.Matrix
  const isSlicedByRate = sliceByQuestionType === QuestionType.Rate || sliceByQuestionType === QuestionType.RateOnly
  const isMultiSelect = questionType === QuestionType.MultiSelect
  return {
    useHTML: true,
    formatter: function () {
      const selectedPoint: Highcharts.Point = this as unknown as Highcharts.Point

      const yValue = isWeightedAvg ? selectedPoint.y! : Highcharts.numberFormat(selectedPoint.y!, 0) + '%'
      // This could be "Male" from gender slice for example
      let demographicName = selectedPoint.x.toString()
      // Sliced x axis values come in something like "Male <br/> n=100"
      const demographicSplitIndex = demographicName.indexOf(' <br/>')
      demographicName = demographicSplitIndex < 0 ? demographicName : demographicName.substr(0, demographicSplitIndex)

      const allOptions = isRate && questionType !== QuestionType.Matrix
        ? selectedPoint.series.chart.legend.allItems.slice().reverse()
        : selectedPoint.series.chart.legend.allItems
      // Whatever option the respondent selected. For example: "Every day"
      const optionSelection = allOptions[selectedPoint.series.index].name

      const segmentTotalOptionKey = isRate
        ? removeOptionLabelsForRateAxis(optionSelection)
        : optionSelection

      demographicName = isSlicedByRate
        ? removeOptionLabelsForRateAxis(demographicName)
        : demographicName

      const totalsForDemographic = segmentTotals[demographicName]
      const respondentCountText = `${totalsForDemographic[segmentTotalOptionKey] || 0} ${isMultiSelect ? 'responses' : 'respondents'}`

      const isLongLabel = `${demographicName}: ${optionSelection}`.length > 50

      return `${generateTooltipContainerDiv(isLongLabel)}<p style="font-size: 1.8rem; line-height: 1.6"><b>${yValue}</b></p>
              ${generateOptionInformationSpan(isLongLabel)}<b>${demographicName}: </b>${optionSelection}</span>
              ${isWeightedAvg ? '' : generateResponseCountDiv(respondentCountText)}`
    }
  }
}

export const indexChartTooltipFormatter = (indexTotals: Record<string, number>, isRank = false, isRate = false, isMultiSelect = false): Highcharts.TooltipOptions => {
  return {
    useHTML: true,
    formatter: function () {
      const selectedPoint: Highcharts.Point = this as unknown as Highcharts.Point

      const optionSelection = selectedPoint.x.toString()
      const segmentTotalOptionKey = isRate
        ? removeOptionLabelsForRateAxis(optionSelection)
        : optionSelection

      const yValue = isRank ? selectedPoint.y! : Highcharts.numberFormat(selectedPoint.y!, 0) + '%'
      const respondentCountText = `${indexTotals[segmentTotalOptionKey] || 0} ${isMultiSelect ? 'responses' : 'respondents'}`

      const isLongLabel = optionSelection.length > 50

      return `${generateTooltipContainerDiv(isLongLabel)}<p style="font-size: 1.8rem; line-height: 1.6"><b>${yValue}</b></p>
              ${generateOptionInformationSpan(isLongLabel)}${optionSelection}</span>
              ${isRank ? '' : generateResponseCountDiv(respondentCountText)}`
    }
  }
}

export const indexRankChartToolTipFormatter = (indexTotals: Record<string, number>) => indexChartTooltipFormatter(indexTotals, true)

export const indexRateChartToolTipFormatter = (indexTotals: Record<string, number>) => indexChartTooltipFormatter(indexTotals, false, true)

export const indexMultiSelectChartToolTipFormatter = (indexTotals: Record<string, number>) => indexChartTooltipFormatter(indexTotals, false, false, true)

export const wordCloudTooltipFormatter = (frequencyMap: Record<string, number>): Highcharts.TooltipOptions => {
  return {
    useHTML: true,
    formatter: function () {
      const selectedPoint: Highcharts.PointOptionsObject = this.point as unknown as Highcharts.PointOptionsObject

      const optionSelection = selectedPoint.name?.toString() || ''
      const yValue = frequencyMap[optionSelection] || selectedPoint.weight || 0

      const isLongLabel = optionSelection.length > 50

      return `${generateTooltipContainerDiv(isLongLabel)}<p style="font-size: 1.8rem; line-height: 1.6"><b>${optionSelection}</b></p>
              ${generateOptionInformationSpan(isLongLabel)}${yValue} occurrences</span>`
    }
  }
}

const generateResponseCountDiv = (count: string) => {
  return `<div style="margin-top: .5rem"><span>${count}</span></div>`
}

/**
 * If the axis names are long, we need the tooltip to wrap. We have to apply styles only in this case because
 * it creates weird wrapping issues when the white-space: pre-line is always on and you don't explicitly set a width.
 * @param isLongLabel
 */
const generateOptionInformationSpan = (isLongLabel: boolean) => {
  return `<span ${isLongLabel ? 'style="white-space: pre-line !important"' : ''}>`
}

/**
 * If the axis names are long, we want to limit how wide the tooltip can get, so we set an explicit width on it
 * of 200px. We don't want to always set it, otherwise very short tooltips have a lot of whitespace.
 * @param isLongLabel
 */
const generateTooltipContainerDiv = (isLongLabel: boolean) => {
  return `<div ${isLongLabel ? 'style="width: 200px !important"' : ''}>`
}

/**
 * We need to remove the option labels from the axis name if it is rate. This is because segmentTotals is keyed
 * by the options before adding the labels. Something we can explore in the future is mutating the rate question
 * responses before any processing. That way no manual addition or removal of labels is needed.
 * @param optionName The ranked option name to remove stuff from (potentially) ... for example: "5 Extremely interested"
 */
const removeOptionLabelsForRateAxis = (optionName: string) => {
  if (optionName === QuestionOptions.NA) return optionName

  const optionNameSplit = optionName.indexOf(' ')
  optionName = optionNameSplit < 0 ? optionName : optionName.substr(0, optionNameSplit)

  return optionName
}
